// Enhanced authentication hooks for better session management
import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import i18n from '../i18n';
import { authAPI, setGlobalLogoutCallback } from '../services/api';
import { User, LoginCredentials, RegisterData, AuthResponse } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle: (token: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const authCheckSeq = useRef(0);

  // Sync UI language with the authenticated user's saved preference
  useEffect(() => {
    const lang = user?.preferred_language;
    if (lang && lang.split('-')[0] !== (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]) {
      i18n.changeLanguage(lang);
    }
  }, [user?.preferred_language]);

  // Verify auth status on mount via HttpOnly cookie (no localStorage needed)
  useEffect(() => {
    const currentSeq = ++authCheckSeq.current;
    authAPI.getCurrentUser()
      .then(userData => {
        if (authCheckSeq.current === currentSeq) {
          setUser(userData);
        }
      })
      .catch(() => {
        if (authCheckSeq.current === currentSeq) {
          setUser(null);
        }
      })
      .finally(() => {
        if (authCheckSeq.current === currentSeq) {
          setIsLoading(false);
        }
      });
  }, []);

  // Kept for API compatibility; silent refresh is handled transparently by the api.ts interceptor
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      await authAPI.refreshToken();
      return true;
    } catch {
      setUser(null);
      return false;
    }
  }, []);

  const loginMutation = useMutation(authAPI.login, {
    onSuccess: (response: AuthResponse) => {
      authCheckSeq.current += 1;
      setUser(response.user);
      setIsLoading(false);
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('Login failed:', error);
    }
  });

  const googleLoginMutation = useMutation(authAPI.loginWithGoogle, {
    onSuccess: (response: AuthResponse) => {
      authCheckSeq.current += 1;
      setUser(response.user);
      setIsLoading(false);
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('Google login failed:', error);
    }
  });

  const registerMutation = useMutation(authAPI.register, {
    onSuccess: (userData: User) => {
      // After registration, user needs to login
      console.log('Registration successful:', userData);
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    }
  });

  const login = async (credentials: LoginCredentials) => {
    await loginMutation.mutateAsync(credentials);
  };

  const loginWithGoogle = async (token: string) => {
    await googleLoginMutation.mutateAsync(token);
  };

  const register = async (userData: RegisterData) => {
    await registerMutation.mutateAsync(userData);
  };

  const logout = async () => {
    try {
      if (user) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      setUser(null);
      queryClient.clear();
    }
  };

  // Register logout function with API service for automatic logout on session expiry
  useEffect(() => {
    setGlobalLogoutCallback(() => {
      setUser(null);
      queryClient.clear();
    });
  }, [queryClient]);

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      queryClient.invalidateQueries('current-user');
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading: isLoading || loginMutation.isLoading || googleLoginMutation.isLoading || registerMutation.isLoading,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshUser,
    refreshToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
